import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getStores } from '../api/client';
import { 
  createBreakEvenData, 
  getBreakEvenData, 
  updateBreakEvenData, 
  deleteBreakEvenData,
  getBreakEvenAnalysis 
} from '../api/client';
import { 
  PlusIcon, 
  TrashIcon, 
  CalculatorIcon, 
  CheckIcon, 
  XMarkIcon,
  PencilIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { formatCurrency, formatDualCurrency } from '../utils/formatters';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ReferenceLine,
  Area,
  ComposedChart
} from 'recharts';

const BreakEvenAnalysis = () => {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [breakEvenData, setBreakEvenData] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingData, setEditingData] = useState(null);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [newBreakEven, setNewBreakEven] = useState({
    store_id: '',
    period_month: currentMonth,
    period_year: currentYear,
    fixed_costs: '',
    variable_cost_percentage: '',
    average_selling_price: ''
  });

  const [editBreakEven, setEditBreakEven] = useState({
    store_id: '',
    period_month: currentMonth,
    period_year: currentYear,
    fixed_costs: '',
    variable_cost_percentage: '',
    average_selling_price: ''
  });

  const [analysisFilters, setAnalysisFilters] = useState({
    store_id: '',
    period_month: currentMonth,
    period_year: currentYear
  });

  const months = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
  ];

  useEffect(() => {
    loadStores();
    loadBreakEvenData();
  }, []);

  const loadStores = async () => {
    try {
      const storesData = await getStores();
      setStores(storesData);
    } catch (err) {
      console.error('Error loading stores:', err);
    }
  };

  const loadBreakEvenData = async () => {
    try {
      setLoading(true);
      const data = await getBreakEvenData();
      setBreakEvenData(data);
      setError('');
    } catch (err) {
      console.error('Error loading break-even data:', err);
      setError('Error al cargar los datos de punto de equilibrio');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBreakEven = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setError('');
    setSuccess('');

    try {
      const dataToSubmit = {
        ...newBreakEven,
        fixed_costs: parseFloat(newBreakEven.fixed_costs),
        variable_cost_percentage: parseFloat(newBreakEven.variable_cost_percentage),
        average_selling_price: newBreakEven.average_selling_price ? parseFloat(newBreakEven.average_selling_price) : null
      };

      await createBreakEvenData(dataToSubmit);
      setSuccess('Datos de punto de equilibrio creados exitosamente');
      setNewBreakEven({
        store_id: '',
        period_month: currentMonth,
        period_year: currentYear,
        fixed_costs: '',
        variable_cost_percentage: '',
        average_selling_price: ''
      });
      setShowCreateModal(false);
      loadBreakEvenData();
    } catch (err) {
      console.error('Error creating break-even data:', err);
      setError(err.response?.data?.detail || 'Error al crear los datos de punto de equilibrio');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditBreakEven = async (e) => {
    e.preventDefault();
    if (!editingData) return;
    
    setEditLoading(true);
    setError('');
    setSuccess('');

    try {
      const dataToSubmit = {
        ...editBreakEven,
        fixed_costs: parseFloat(editBreakEven.fixed_costs),
        variable_cost_percentage: parseFloat(editBreakEven.variable_cost_percentage),
        average_selling_price: editBreakEven.average_selling_price ? parseFloat(editBreakEven.average_selling_price) : null
      };

      await updateBreakEvenData(editingData.id, dataToSubmit);
      setSuccess('Datos de punto de equilibrio actualizados exitosamente');
      setShowEditModal(false);
      setEditingData(null);
      loadBreakEvenData();
    } catch (err) {
      console.error('Error updating break-even data:', err);
      setError(err.response?.data?.detail || 'Error al actualizar los datos de punto de equilibrio');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteBreakEven = async (breakEvenId, storeName, month, year) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar los datos de punto de equilibrio para "${storeName}" - ${month}/${year}?`)) {
      return;
    }

    try {
      await deleteBreakEvenData(breakEvenId);
      setSuccess('Datos de punto de equilibrio eliminados exitosamente');
      loadBreakEvenData();
    } catch (err) {
      console.error('Error deleting break-even data:', err);
      setError('Error al eliminar los datos de punto de equilibrio');
    }
  };

  const handleAnalyze = async () => {
    if (!analysisFilters.store_id) {
      setError('Por favor selecciona una tienda para el análisis');
      return;
    }

    setAnalysisLoading(true);
    setError('');

    try {
      const analysisResult = await getBreakEvenAnalysis(
        analysisFilters.store_id,
        analysisFilters.period_month,
        analysisFilters.period_year
      );
      setAnalysis(analysisResult);
      setSuccess('Análisis de punto de equilibrio calculado exitosamente');
    } catch (err) {
      console.error('Error calculating break-even analysis:', err);
      setError(err.response?.data?.detail || 'Error al calcular el análisis de punto de equilibrio');
      setAnalysis(null);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const openEditModal = (data) => {
    setEditingData(data);
    setEditBreakEven({
      store_id: data.store_id,
      period_month: data.period_month,
      period_year: data.period_year,
      fixed_costs: data.fixed_costs.toString(),
      variable_cost_percentage: data.variable_cost_percentage.toString(),
      average_selling_price: data.average_selling_price ? data.average_selling_price.toString() : ''
    });
    setShowEditModal(true);
    resetMessages();
  };

  const resetMessages = () => {
    setError('');
    setSuccess('');
  };

  const getStoreName = (storeId) => {
    const store = stores.find(s => s.id === storeId);
    return store ? store.name : 'Tienda Desconocida';
  };

  const getMonthName = (monthNumber) => {
    const month = months.find(m => m.value === monthNumber);
    return month ? month.label : monthNumber.toString();
  };

  // Generate chart data for break-even visualization
  const generateChartData = () => {
    if (!analysis) return [];
    
    const maxUnits = Math.max(analysis.break_even_units * 1.5, analysis.current_units * 1.2, 100);
    const step = maxUnits / 20;
    const data = [];
    
    for (let units = 0; units <= maxUnits; units += step) {
      const revenue = units * (analysis.break_even_revenue / analysis.break_even_units || 0);
      const variableCosts = revenue * ((100 - analysis.margin_contribution_percentage) / 100);
      const totalCosts = variableCosts + (analysis.break_even_revenue - (analysis.break_even_units * analysis.margin_contribution_per_unit));
      
      data.push({
        units: Math.round(units),
        revenue,
        totalCosts,
        profit: revenue - totalCosts
      });
    }
    
    return data;
  };

  const chartData = generateChartData();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between mb-8">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            <CalculatorIcon className="inline-block w-8 h-8 mr-3 text-blue-600" />
            Análisis de Punto de Equilibrio
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Calcula cuánto necesitas vender para cubrir todos tus costos
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={() => {
              resetMessages();
              setShowCreateModal(true);
            }}
            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuevos Datos
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XMarkIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <CheckIcon className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Section */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            <ChartBarIcon className="inline-block w-5 h-5 mr-2" />
            Realizar Análisis
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Tienda</label>
              <select
                value={analysisFilters.store_id}
                onChange={(e) => setAnalysisFilters({...analysisFilters, store_id: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Seleccionar tienda</option>
                {stores.map(store => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Mes</label>
              <select
                value={analysisFilters.period_month}
                onChange={(e) => setAnalysisFilters({...analysisFilters, period_month: parseInt(e.target.value)})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Año</label>
              <select
                value={analysisFilters.period_year}
                onChange={(e) => setAnalysisFilters({...analysisFilters, period_year: parseInt(e.target.value)})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {[2024, 2025, 2026].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={handleAnalyze}
                disabled={analysisLoading}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                {analysisLoading ? 'Calculando...' : 'Analizar'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Analysis Results */}
      {analysis && (
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">
              Resultados del Análisis - {getStoreName(analysisFilters.store_id)} 
              ({getMonthName(analysisFilters.period_month)} {analysisFilters.period_year})
            </h3>
            
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-900">
                  {Math.round(analysis.break_even_units)}
                </div>
                <div className="text-sm text-blue-600">Unidades en Equilibrio</div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-900">
                  {formatCurrency(analysis.break_even_revenue)}
                </div>
                <div className="text-sm text-green-600">Ingresos en Equilibrio</div>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-purple-900">
                  {analysis.margin_contribution_percentage.toFixed(1)}%
                </div>
                <div className="text-sm text-purple-600">Margen de Contribución</div>
              </div>
              
              <div className={`p-4 rounded-lg ${analysis.safety_margin_percentage >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className={`text-2xl font-bold ${analysis.safety_margin_percentage >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                  {analysis.safety_margin_percentage.toFixed(1)}%
                </div>
                <div className={`text-sm ${analysis.safety_margin_percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  Margen de Seguridad
                </div>
              </div>
            </div>

            {/* Current vs Target */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Situación Actual</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Ventas Actuales:</span>
                    <span className="text-sm font-medium">{formatCurrency(analysis.current_sales)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Unidades Vendidas:</span>
                    <span className="text-sm font-medium">{analysis.current_units}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Diferencia vs Equilibrio</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Diferencia en Ingresos:</span>
                    <span className={`text-sm font-medium ${analysis.surplus_deficit_revenue >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {analysis.surplus_deficit_revenue >= 0 ? '+' : ''}{formatCurrency(analysis.surplus_deficit_revenue)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Diferencia en Unidades:</span>
                    <span className={`text-sm font-medium ${analysis.surplus_deficit_units >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {analysis.surplus_deficit_units >= 0 ? '+' : ''}{Math.round(analysis.surplus_deficit_units)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Break-Even Chart */}
            {chartData.length > 0 && (
              <div className="mb-8">
                <h4 className="font-medium text-gray-900 mb-4">Gráfico de Punto de Equilibrio</h4>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="units" 
                        label={{ value: 'Unidades Vendidas', position: 'insideBottom', offset: -10 }}
                      />
                      <YAxis 
                        label={{ value: 'Monto ($)', angle: -90, position: 'insideLeft' }}
                        tickFormatter={(value) => `$${(value/1000).toFixed(0)}K`}
                      />
                      <Tooltip 
                        formatter={(value, name) => [formatCurrency(value), name]}
                        labelFormatter={(value) => `${value} unidades`}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="#10b981" 
                        strokeWidth={2}
                        name="Ingresos"
                        dot={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="totalCosts" 
                        stroke="#ef4444" 
                        strokeWidth={2}
                        name="Costos Totales"
                        dot={false}
                      />
                      <ReferenceLine 
                        x={Math.round(analysis.break_even_units)} 
                        stroke="#6366f1" 
                        strokeDasharray="8 8"
                        label={{ value: "Punto de Equilibrio", position: "topRight" }}
                      />
                      <ReferenceLine 
                        x={analysis.current_units} 
                        stroke="#f59e0b" 
                        strokeDasharray="4 4"
                        label={{ value: "Posición Actual", position: "topRight" }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Break-Even Data Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Datos de Punto de Equilibrio Configurados
          </h3>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-sm text-gray-600">Cargando datos...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tienda
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Período
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Costos Fijos
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      % Costo Variable
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Precio Promedio
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {breakEvenData.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {getStoreName(item.store_id)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getMonthName(item.period_month)} {item.period_year}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(item.fixed_costs)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.variable_cost_percentage}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.average_selling_price ? formatCurrency(item.average_selling_price) : 'Auto-calculado'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => openEditModal(item)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200"
                          >
                            <PencilIcon className="h-3 w-3 mr-1" />
                            Editar
                          </button>
                          <button
                            onClick={() => handleDeleteBreakEven(
                              item.id, 
                              getStoreName(item.store_id), 
                              item.period_month, 
                              item.period_year
                            )}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200"
                          >
                            <TrashIcon className="h-3 w-3 mr-1" />
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {breakEvenData.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-sm text-gray-500">
                        No hay datos de punto de equilibrio configurados.
                        <br />
                        <button
                          onClick={() => setShowCreateModal(true)}
                          className="mt-2 text-blue-600 hover:text-blue-500"
                        >
                          Agregar el primero
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Crear Datos de Punto de Equilibrio
              </h3>
              
              <form onSubmit={handleCreateBreakEven} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Tienda *
                  </label>
                  <select
                    required
                    value={newBreakEven.store_id}
                    onChange={(e) => setNewBreakEven({...newBreakEven, store_id: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Seleccionar tienda</option>
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Mes *
                    </label>
                    <select
                      required
                      value={newBreakEven.period_month}
                      onChange={(e) => setNewBreakEven({...newBreakEven, period_month: parseInt(e.target.value)})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      {months.map(month => (
                        <option key={month.value} value={month.value}>{month.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Año *
                    </label>
                    <select
                      required
                      value={newBreakEven.period_year}
                      onChange={(e) => setNewBreakEven({...newBreakEven, period_year: parseInt(e.target.value)})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      {[2024, 2025, 2026].map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Costos Fijos Mensuales ($) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={newBreakEven.fixed_costs}
                    onChange={(e) => setNewBreakEven({...newBreakEven, fixed_costs: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="15000.00"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Alquiler, salarios, servicios, etc.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Porcentaje de Costo Variable (%) *
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    required
                    value={newBreakEven.variable_cost_percentage}
                    onChange={(e) => setNewBreakEven({...newBreakEven, variable_cost_percentage: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="65.0"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    % del costo del producto sobre la venta
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Precio Promedio de Venta ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={newBreakEven.average_selling_price}
                    onChange={(e) => setNewBreakEven({...newBreakEven, average_selling_price: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="25.00"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Opcional - se calculará automáticamente de las ventas
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={createLoading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {createLoading ? 'Creando...' : 'Crear Datos'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Editar Datos - {getStoreName(editingData.store_id)}
              </h3>
              
              <form onSubmit={handleEditBreakEven} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Tienda *
                  </label>
                  <select
                    required
                    value={editBreakEven.store_id}
                    onChange={(e) => setEditBreakEven({...editBreakEven, store_id: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Mes *
                    </label>
                    <select
                      required
                      value={editBreakEven.period_month}
                      onChange={(e) => setEditBreakEven({...editBreakEven, period_month: parseInt(e.target.value)})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      {months.map(month => (
                        <option key={month.value} value={month.value}>{month.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Año *
                    </label>
                    <select
                      required
                      value={editBreakEven.period_year}
                      onChange={(e) => setEditBreakEven({...editBreakEven, period_year: parseInt(e.target.value)})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      {[2024, 2025, 2026].map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Costos Fijos Mensuales ($) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={editBreakEven.fixed_costs}
                    onChange={(e) => setEditBreakEven({...editBreakEven, fixed_costs: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Porcentaje de Costo Variable (%) *
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    required
                    value={editBreakEven.variable_cost_percentage}
                    onChange={(e) => setEditBreakEven({...editBreakEven, variable_cost_percentage: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Precio Promedio de Venta ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editBreakEven.average_selling_price}
                    onChange={(e) => setEditBreakEven({...editBreakEven, average_selling_price: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingData(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={editLoading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {editLoading ? 'Actualizando...' : 'Actualizar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BreakEvenAnalysis;